import { Location } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TenantService } from '@common-modules/shared/config/tenant.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoginOptions, OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { SettingsService } from '../../config/settings.service';
import { GlobalsService } from '../../services/globals.service';
import { LicenceModules } from '../models/licence-modules.enum';
import { User } from '../models/user';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private userSubject: BehaviorSubject<User>;
  private refreshTokenTimeout;
  public get userValue(): User {
    return this.userSubject.value;
  }

  public userCode;
  private _userCode$ = new ReplaySubject(1);
  readonly userCode$ = this._userCode$.asObservable();

  public get email(): string {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) {
      return null;
    }
    return claims['email'];
  }
  public user: Observable<User>;

  private _accessToken: string;
  private _accessToken$ = new ReplaySubject<string>(1);
  readonly accessToken$ = this._accessToken$.asObservable();
  tenantName: string;
  private readonly _httpWithoutInterceptors: HttpClient;

  constructor(
    private oauthService: OAuthService,
    private settingsService: SettingsService,
    private _globalsService: GlobalsService,
    private readonly _location: Location,
    private readonly _route: ActivatedRoute,
    httpBackend: HttpBackend, // HttpBackend to bypass interceptors
    private readonly _tenantService: TenantService,
    private readonly _destroyRef: DestroyRef
  ) {
    // Create an HttpClient instance that bypasses interceptors
    this._httpWithoutInterceptors = new HttpClient(httpBackend);
    this.userSubject = new BehaviorSubject<User>(null);
    this.user = this.userSubject.asObservable();

    const token = this.oauthService.getAccessToken();
    if (token) {
      this._accessToken$.next(token);
      this._accessToken = token;
      this.getUserCode();
    } else {
      this.oauthService.events
        .pipe(
          untilDestroyed(this),
          filter((event) => {
            const result = Boolean(
              ['discovery_document_loaded', 'token_received'].find((type) => type === event.type)
            );
            return result;
          }),
          map((_) => {
            const token = this.oauthService.getAccessToken();
            return token;
          }),
          filter((token) => token !== null),
          take(1)
        )
        .subscribe((token) => {
          this._accessToken$.next(token);
          this._accessToken = token;

          this.getTenantName().subscribe((res) => {
            this.tenantName = res;
          });

          this.getUserCode();
        });
    }

    this._tenantService.logoutIfNoTenant$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((urlTenant) => {
      this.logoutIfNoTenant(urlTenant);
    });
  }

  private logoutIfNoTenant(urlTenant: string): void {
    if (this.tenantName != undefined && this.tenantName != urlTenant) {
      this.logout();
    }
  }

  getTenantName(): Observable<string> {
    const tenantId = this.oauthService.getIdentityClaims()['tenantid'];
    if (tenantId) {
      return this._httpWithoutInterceptors.get<{ name: string }>(
        `${this.settingsService.apis.default.url}/api/abp/multi-tenancy/tenants/by-id/${tenantId}`
      ).pipe(map(result => result?.name));
    }
    return of(null);
  }

  customLoadDiscoveryDocumentAndTryLogin(): Promise<boolean> {
    if (this.oauthService.hasValidAccessToken()) {
      return new Promise(() => true);
    }

    this.oauthService.configure(this.settingsService.oAuthConfig);
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    const queryString = window.location.search;
    const options: LoginOptions = {
      customHashFragment: queryString ? `#${queryString}` : null,
    };
    return this.oauthService.loadDiscoveryDocumentAndLogin(options);
  }

  logout(beforeLogoutFn?: () => any) {
    const isLogged = this.oauthService.hasValidAccessToken();
    if (isLogged) {
      console.log(isLogged);
    }

    if (beforeLogoutFn) {
      beforeLogoutFn();
    }

    this.oauthService
      .revokeTokenAndLogout(false)
      .catch((exception) => {
        console.error(exception);
        this.oauthService.logOut(false);
      })
      .then((_) => {
        this.oauthService.logOut(false);
      });
  }

  getBearerToken(): Observable<string> {
    return this.accessToken$.pipe(
      map((token) => {
        return `Bearer ${token}`;
      })
    );
  }

  mustLogIn(): boolean {
    return !this.oauthService.hasValidAccessToken() && !this.isOauthCallbackRoute();
  }

  getAuthRedirectUri(): string {
    const redirectUri = this.settingsService?.oAuthConfig?.redirectUri;

    if (!redirectUri) {
      return null;
    }

    const fragments = redirectUri.split('/');
    return fragments[fragments.length - 1];
  }

  isOauthCallbackRoute(urlPath?: string): boolean {
    const path = urlPath ?? this._location.path();
    const params = this._route.snapshot.queryParamMap;
    const noParamsRoute = path.split('?')[0];
    const redirectUri = this.getAuthRedirectUri();
    return (
      (redirectUri && path.includes(redirectUri)) ||
      ((noParamsRoute === '' || noParamsRoute === '/') && params.has('code') && params.has('state'))
    );
  }

  // Only use after being sure that token initialization has been performed.
  get currentToken(): string {
    return this._accessToken;
  }

  private refreshToken() {
    return this._httpWithoutInterceptors
      .post<any>(
        `${this.settingsService.apis.default.url}/users/refresh-token`,
        {},
        { withCredentials: true }
      )
      .pipe(
        map((user) => {
          this.userSubject.next(user);
          this.startRefreshTokenTimer();
          return user;
        })
      );
  }

  /**
   * Ensures that the access token is available. If not, performs login again.
   */
  ensureTokenAvailable(): void {
    if (!this.oauthService.hasValidAccessToken()) {
      this.customLoadDiscoveryDocumentAndTryLogin();
    }
  }

  getEnabledModulesFromToken() {
    //TODO: [Pending] inspect token to extract enabled modules by licence and returns it as an array of LicenceModules
    const data = this.oauthService.getAccessToken();
    const jwtToken = JSON.parse(atob(data.split('.')[1]));

    const modules = [LicenceModules.ALC, LicenceModules.BI, LicenceModules.Pressure];
    return modules;
  }

  private getUserCode() {
    this._globalsService.getCurrentUser().subscribe((user) => {
      this.userCode = user?.userCode;
      this._userCode$.next(this.userCode);
    });
  }

  private startRefreshTokenTimer() {
    // parse json object from base64 encoded jwt token
    const jwtToken = JSON.parse(atob(this.userValue.jwtToken.split('.')[1]));

    // set a timeout to refresh the token a minute before it expires
    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - 60 * 1000;
    this.refreshTokenTimeout = setTimeout(() => this.refreshToken().subscribe(), timeout);
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }
}
